import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ChevronRight, FileText, History, Landmark, Loader2, Radar, RefreshCw, Search, Settings2 } from "lucide-react";

import {
  type GrantRadarDecision,
  type GrantRadarFinding,
  type GrantRadarProfilePayload,
  type GrantRadarRun,
  type GrantRadarState,
  fetchGrantRadar,
  saveGrantRadarProfile,
  startGrantRadarRun,
  updateGrantRadarFinding,
} from "@/api/grant_radar";
import { MODULE_WORKSPACE_X } from "@/components/module_summary/moduleWorkspaceLayout";
import { AVAILABILITY, GrantRadarFindingDialog, deadlineLabel } from "@/components/grant_suite/GrantRadarFindingDialog";
import { GrantRadarProfileDialog } from "@/components/grant_suite/GrantRadarProfileDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { invalidateGrantSuitePipeline } from "@/lib/grantSuiteQueryKeys";
import { cn } from "@/lib/utils";

// The server resolves the user and workspace team from the session, so the
// municipality identifies the cached profile. Signing in as someone else reloads the SPA.
const radarKey = (municipalityId: number) => ["grant_radar", municipalityId] as const;

// The shared Alert absolutely positions its icon for an AlertTitle layout, which
// sits a description-only line visibly off-center. Lay the icon out inline,
// aligned to the first 20px text line, instead.
const INLINE_ALERT = "flex items-start gap-3 [&>svg]:static [&>svg]:mt-0.5 [&>svg]:shrink-0 [&>svg~*]:pl-0 [&>svg+div]:translate-y-0";

type TabValue = "review" | GrantRadarDecision;

const TABS: { value: TabValue; label: string; empty: string }[] = [
  { value: "review", label: "To review", empty: "Nothing left to review. Run a check to look for more grants." },
  { value: "saved", label: "Saved", empty: "Grants you save appear here." },
  { value: "does_not_fit", label: "Doesn't fit", empty: "Grants you mark as not a fit appear here. Future checks avoid similar ones." },
  { value: "archived", label: "Archived", empty: "Archived grants appear here so you can come back to them later." },
];

// Grants users can apply for come first; the badge in each row says which status it has.
const AVAILABILITY_ORDER: Record<GrantRadarFinding["availability"], number> = {
  open: 0,
  rolling: 0,
  forecast: 1,
  unknown: 2,
  prior_round: 3,
  closed: 3,
};

function formatTime(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

// Plain-language versions of the agent's tool calls, so users can follow a check without URLs or tool names.
function describeStep(entry: GrantRadarRun["activity"][number]) {
  const detail = entry.detail?.trim();
  switch (entry.tool) {
    case "web_search":
      return { icon: Search, text: detail ? `Searched the web for “${detail}”` : "Searched the web" };
    case "grants_gov_search":
      return { icon: Landmark, text: detail ? `Searched Grants.gov for “${detail}”` : "Searched Grants.gov" };
    case "grants_gov_opportunity":
      return { icon: Landmark, text: "Opened a federal grant listing on Grants.gov" };
    case "search_past_grants":
      return { icon: History, text: detail ? `Checked your past grants for “${detail}”` : "Checked your past grants" };
    case "read_page": {
      const host = detail ? hostOf(detail) : null;
      const kind = detail?.toLowerCase().endsWith(".pdf") ? "a document" : "a page";
      return { icon: FileText, text: host ? `Read ${kind} on ${host}` : `Read ${kind}` };
    }
    default:
      return { icon: Search, text: "Checking sources" };
  }
}

function GrantRadarProgress({ applicant, activity }: { applicant: string | null | undefined; activity: GrantRadarRun["activity"] }) {
  const steps = activity.slice(-6).reverse();
  return (
    <section aria-live="polite" className="rounded-xl border bg-card px-6 py-10">
      <div className="mx-auto flex max-w-xl flex-col items-center text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        </div>
        <h3 className="mt-4 text-lg font-semibold">Checking funders{applicant ? ` for ${applicant}` : ""}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Grant Radar is searching for grants and reading funders' own pages to confirm who can apply. This usually takes a few minutes.
          You can leave this page; your results will be here when you come back.
        </p>
      </div>
      <ul className="mx-auto mt-8 max-w-xl space-y-1">
        {steps.length === 0 ? (
          <li className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground">
            <Search className="h-4 w-4 shrink-0" aria-hidden />
            Getting started
          </li>
        ) : (
          steps.map((entry, index) => {
            const step = describeStep(entry);
            return (
              <li
                key={entry.at + entry.tool + (entry.detail ?? "")}
                className={cn(
                  "flex items-start gap-3 rounded-md px-3 py-2 text-sm",
                  index === 0 ? "bg-muted/60 text-foreground" : "text-muted-foreground",
                )}
              >
                <step.icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span className="min-w-0">{step.text}</span>
              </li>
            );
          })
        )}
      </ul>
    </section>
  );
}

export function GrantRadarPanel({ municipalityId }: { municipalityId: number }) {
  const queryClient = useQueryClient();
  const [profileOpen, setProfileOpen] = useState(false);
  const [tab, setTab] = useState<TabValue>("review");
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: radarKey(municipalityId),
    queryFn: () => fetchGrantRadar(municipalityId),
    refetchInterval: (current) => {
      const status = current.state.data?.latest_run?.status;
      return status === "queued" || status === "running" ? 5000 : false;
    },
  });
  const data = query.data;
  const setData = (next: GrantRadarState) => queryClient.setQueryData(radarKey(municipalityId), next);

  const saveProfile = useMutation({
    mutationFn: (payload: GrantRadarProfilePayload) => saveGrantRadarProfile(municipalityId, payload),
    onSuccess: (next) => {
      setData(next);
      setProfileOpen(false);
    },
  });

  const run = useMutation({
    mutationFn: () => startGrantRadarRun(municipalityId),
    onSuccess: (next) => setData(next),
    onMutate: () => setActionError(null),
    onError: (error: Error) => {
      setActionError(error.message);
      query.refetch();
    },
  });

  const [openId, setOpenId] = useState<number | null>(null);
  const updateFinding = useMutation({
    mutationFn: ({ finding, decision, reason }: { finding: GrantRadarFinding; decision: GrantRadarDecision | null; reason?: string }) =>
      updateGrantRadarFinding(municipalityId, finding.id, { decision, decision_reason: reason ?? null }),
    onSuccess: ({ finding }, { finding: before }) => {
      // Reviewing walks through To review: after a decision there, open the next grant still waiting.
      const next = !before.decision ? groups.review.find((item) => item.id !== finding.id) : undefined;
      queryClient.setQueryData<GrantRadarState>(radarKey(municipalityId), (current) =>
        current ? { ...current, findings: current.findings.map((item) => (item.id === finding.id ? finding : item)) } : current,
      );
      setOpenId(next ? next.id : null);
      if (finding.decision === "saved") invalidateGrantSuitePipeline(queryClient, municipalityId);
    },
  });

  const groups = useMemo(() => {
    const findings = [...(data?.findings ?? [])].sort(
      (a, b) => AVAILABILITY_ORDER[a.availability] - AVAILABILITY_ORDER[b.availability],
    );
    return {
      review: findings.filter((finding) => !finding.decision),
      saved: findings.filter((finding) => finding.decision === "saved"),
      does_not_fit: findings.filter((finding) => finding.decision === "does_not_fit"),
      archived: findings.filter((finding) => finding.decision === "archived"),
    };
  }, [data?.findings]);

  if (query.isLoading) {
    return (
      <div className={`${MODULE_WORKSPACE_X} space-y-3 pt-5`}>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className={`${MODULE_WORKSPACE_X} pt-5`}>
        <Alert variant="destructive" className={INLINE_ALERT}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{(query.error as Error | null)?.message ?? "Grant Radar could not load. Refresh the page to try again."}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const latest = data?.latest_run;
  const active = latest?.status === "queued" || latest?.status === "running";
  const lastSuccess = data?.last_successful_run;
  const ready = Boolean(data.profile?.ready);
  const applicant = data.profile?.applicant_name;
  const waiting = groups.review.length;
  const openFinding = data.findings.find((finding) => finding.id === openId) ?? null;

  return (
    <div className="ordinal-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className={`${MODULE_WORKSPACE_X} space-y-4 pb-6 pt-4 sm:pt-5`}>
        <section className="space-y-3 rounded-xl border bg-card p-4 sm:p-5">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Radar className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold tracking-tight">Grant Radar</h2>
              <p className="text-sm text-muted-foreground">
                {applicant ? `Grants found for ${applicant}` : "Grants found for your profile"}, checked against funders' own pages. Confirm
                details with the funder before applying.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setProfileOpen(true)}>
              <Settings2 className="mr-1.5 h-4 w-4" />
              {data.profile ? "Edit profile" : "Set up profile"}
            </Button>
            <Button size="sm" disabled={!ready || active || run.isPending || waiting > 0} onClick={() => run.mutate()}>
              {active || run.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
              {active ? "Checking…" : "Check now"}
            </Button>
          </div>
          {data.profile ? (
            <p className="text-xs text-muted-foreground">
              {waiting > 0 && !active
                ? `Review the ${waiting} ${waiting === 1 ? "grant" : "grants"} in To review before running a new check. `
                : ""}
              {lastSuccess
                ? `Last check ${formatTime(lastSuccess.finished_at)} found ${lastSuccess.new_count} new ${lastSuccess.new_count === 1 ? "grant" : "grants"}.`
                : "No completed checks yet."}
            </p>
          ) : null}
        </section>

        {actionError ? (
          <Alert variant="destructive" className={INLINE_ALERT}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        {!data.profile ? (
          <section className="rounded-xl border border-dashed p-6 text-center">
            <h3 className="font-medium">Tell Grant Radar who you are and what you need</h3>
            <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
              Say who you are applying as, such as your department, and the projects or equipment you want funding for. Then run a check to see what you can apply for.
            </p>
            <Button className="mt-4" onClick={() => setProfileOpen(true)}>
              Set up profile
            </Button>
          </section>
        ) : null}

        {latest && active ? <GrantRadarProgress applicant={applicant} activity={latest.activity} /> : null}
        {latest?.status === "failed" ? (
          <Alert variant="destructive" className={INLINE_ALERT}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {latest.error_message}
              {formatTime(latest.finished_at ?? latest.created_at) ? ` (${formatTime(latest.finished_at ?? latest.created_at)})` : ""}
            </AlertDescription>
          </Alert>
        ) : null}

        {data.profile && !active ? (
          <Tabs value={tab} onValueChange={(value) => setTab(value as TabValue)}>
            <TabsList>
              {TABS.map((item) => (
                <TabsTrigger key={item.value} value={item.value}>
                  {item.label} ({groups[item.value].length})
                </TabsTrigger>
              ))}
            </TabsList>
            {TABS.map((item) => (
              <TabsContent key={item.value} value={item.value} className="pt-3">
                {groups[item.value].length > 0 ? (
                  <div className="overflow-hidden rounded-lg border bg-card">
                    <Table aria-label={`${item.label} grants`} className="table-fixed">
                      <colgroup>
                        <col />
                        <col className="hidden w-52 lg:table-column" />
                        <col className="w-36" />
                        <col className="hidden w-48 md:table-column" />
                        <col className="hidden w-32 sm:table-column" />
                        <col className="w-10" />
                      </colgroup>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="px-4">Grant</TableHead>
                          <TableHead className="hidden px-3 lg:table-cell">Funder</TableHead>
                          <TableHead className="px-3">Status</TableHead>
                          <TableHead className="hidden px-3 md:table-cell">Amount</TableHead>
                          <TableHead className="hidden px-3 sm:table-cell">Deadline</TableHead>
                          <TableHead>
                            <span className="sr-only">Open grant</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groups[item.value].map((finding) => (
                          <TableRow key={finding.id} className="group cursor-pointer" onClick={() => setOpenId(finding.id)}>
                            <TableCell className="px-4 py-2">
                              <Button
                                variant="ghost"
                                className="h-auto w-full flex-col items-start gap-0.5 whitespace-normal px-0 py-2 text-left hover:bg-transparent"
                                aria-label={`${finding.program_name}, ${finding.funder}`}
                              >
                                <span className="line-clamp-2 break-words font-medium">{finding.program_name}</span>
                                <span className="line-clamp-1 text-xs font-normal text-muted-foreground lg:hidden">{finding.funder}</span>
                              </Button>
                            </TableCell>
                            <TableCell className="hidden px-3 text-sm text-muted-foreground lg:table-cell">
                              <span className="line-clamp-2">{finding.funder}</span>
                            </TableCell>
                            <TableCell className="px-3">
                              <Badge variant="secondary" className={cn("font-normal", AVAILABILITY[finding.availability].className)}>
                                {AVAILABILITY[finding.availability].label}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden px-3 text-sm md:table-cell">
                              <span className="line-clamp-2">{finding.award_amount ?? "Not stated"}</span>
                            </TableCell>
                            <TableCell className="hidden px-3 text-sm sm:table-cell">
                              <span className="line-clamp-2">{deadlineLabel(finding)}</span>
                            </TableCell>
                            <TableCell className="pr-4 text-muted-foreground group-hover:text-foreground">
                              <ChevronRight className="h-4 w-4" aria-hidden />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {item.value === "review" && !lastSuccess ? "Run a check to find grants." : item.empty}
                  </p>
                )}
              </TabsContent>
            ))}
          </Tabs>
        ) : null}
      </div>

      <GrantRadarFindingDialog
        finding={openFinding}
        saving={updateFinding.isPending}
        error={updateFinding.error ? (updateFinding.error as Error).message : null}
        onOpenChange={(open) => {
          if (!open) {
            setOpenId(null);
            updateFinding.reset();
          }
        }}
        onDecide={(finding, decision, reason) => updateFinding.mutate({ finding, decision, reason })}
      />
      <GrantRadarProfileDialog
          open={profileOpen}
          onOpenChange={(open) => {
            setProfileOpen(open);
            if (!open) saveProfile.reset();
          }}
          data={data}
          saving={saveProfile.isPending}
          error={saveProfile.error ? (saveProfile.error as Error).message : null}
          onSave={(payload) => saveProfile.mutate(payload)}
        />
    </div>
  );
}
